import React, { useState, useEffect } from 'react';
import {
  Percent,
  Save,
  Edit3,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { useAdminAPI } from '../hooks/useAdminAPI';

interface InterestRate {
  type: 'PRO' | 'Normal';
  rate: number;
  description: string;
  updatedAt?: string;
}


const DEFAULT_RATES: InterestRate[] = [
  { type: 'PRO', rate: 12.5, description: 'Tasa PRO' },
  { type: 'Normal', rate: 18.75, description: 'Tasa Normal' }
];

const TasasInteresSection: React.FC = () => {
  const { getInvestmentRates, updateInvestmentRate, isAuthenticated, accessToken } = useAdminAPI();
  const [rates, setRates] = useState<InterestRate[]>(DEFAULT_RATES);
  const [editingRate, setEditingRate] = useState<'PRO' | 'Normal' | null>(null);
  const [editValues, setEditValues] = useState<{ [key: string]: number }>({
    PRO: DEFAULT_RATES[0].rate,
    Normal: DEFAULT_RATES[1].rate
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getRateColor = (type: 'PRO' | 'Normal') => (type === 'PRO' ? 'text-green-600' : 'text-blue-600');
  const getRateBgColor = (type: 'PRO' | 'Normal') =>
    type === 'PRO' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200';

  const fetchRates = async () => {
    if (!isAuthenticated || !accessToken) {
      setSaveMessage({ 
        type: 'error', 
        message: 'No estás autenticado. Por favor inicia sesión.' 
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setSaveMessage(null);
    try {
      console.log('🔑 Fetching rates with token:', accessToken.substring(0, 20) + '...');
      const data = await getInvestmentRates();
      console.log('📊 Received rates data (raw):', JSON.stringify(data, null, 2));
      
      if (!data || data.length === 0) {
        console.warn('⚠️ No rates received, using default rates');
        setRates(DEFAULT_RATES);
        setEditValues({
          PRO: DEFAULT_RATES[0].rate,
          Normal: DEFAULT_RATES[1].rate
        });
        return;
      }
      
      const parsed = data.map(row => {
        console.log('🔍 Processing row:', row);
        // Access as any to check both snake_case and camelCase formats
        const rowAny = row as any;
        // Normalize type to uppercase for comparison
        const normalizedType: 'PRO' | 'Normal' = row.type?.toUpperCase() === 'PRO' ? 'PRO' : 'Normal';
        const rateValue = row.interest_rate ?? rowAny.interestRate ?? rowAny.rate ?? null;
        console.log(`  - Type: ${row.type} -> ${normalizedType}`);
        console.log(`  - Rate: ${rateValue} (from interest_rate: ${row.interest_rate}, interestRate: ${rowAny.interestRate})`);
        
        return {
          type: normalizedType,
          rate: rateValue,
          description: normalizedType === 'PRO' ? 'Tasa PRO' : 'Tasa Normal',
          updatedAt: row.updated_at || rowAny.updatedAt
        } as InterestRate;
      });
      console.log('✅ Parsed rates:', parsed);
      setRates(parsed);
      setEditValues({
        PRO: parsed.find(r => r.type === 'PRO')?.rate ?? DEFAULT_RATES[0].rate,
        Normal: parsed.find(r => r.type === 'Normal')?.rate ?? DEFAULT_RATES[1].rate
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'No se pudieron cargar las tasas';
      setSaveMessage({ type: 'error', message: errorMessage });
      console.error('Error fetching rates:', error);
      // Mantener las tasas por defecto si hay un error
      if (rates.length === 0) {
        setRates(DEFAULT_RATES);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  const handleEdit = (rateType: 'PRO' | 'Normal', currentRate: number) => {
    setEditingRate(rateType);
    setEditValues({ ...editValues, [rateType]: currentRate });
  };

  const handleCancel = (rateType: 'PRO' | 'Normal') => {
    setEditingRate(null);
    const currentRate = rates.find(rate => rate.type === rateType)?.rate ?? DEFAULT_RATES.find(r => r.type === rateType)!.rate;
    setEditValues({ ...editValues, [rateType]: currentRate });
  };

  const handleSave = async (rateType: 'PRO' | 'Normal') => {
    const newRate = editValues[rateType];

    if (newRate < 0 || newRate > 100) {
      setSaveMessage({ type: 'error', message: 'La tasa de interés debe estar entre 0% y 100%' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Update the rate
      await updateInvestmentRate({
        type: rateType.toLowerCase() as 'pro' | 'normal',
        interestRate: newRate
      });
      
      // Immediately fetch the updated rates from the server
      console.log('🔄 Fetching updated rates after save...');
      const data = await getInvestmentRates();
      console.log('📊 Updated rates data (raw):', JSON.stringify(data, null, 2));
      
      if (data && data.length > 0) {
        const parsed = data.map(row => {
          console.log('🔍 Processing row:', row);
          console.log('🔍 Row keys:', Object.keys(row));
          console.log('🔍 Row values:', Object.values(row));
          
          // Access as any to check both snake_case and camelCase formats
          const rowAny = row as any;
          // Normalize type to uppercase for comparison
          const normalizedType: 'PRO' | 'Normal' = row.type?.toUpperCase() === 'PRO' ? 'PRO' : 'Normal';
          const rateValue = row.interest_rate ?? rowAny.interestRate ?? rowAny.rate ?? null;
          
          console.log(`  - Type: ${row.type} -> ${normalizedType}`);
          console.log(`  - interest_rate: ${row.interest_rate}`);
          console.log(`  - interestRate: ${rowAny.interestRate}`);
          console.log(`  - rate: ${rowAny.rate}`);
          console.log(`  - Final rateValue: ${rateValue}`);
          
          if (rateValue === null || rateValue === undefined) {
            console.error('❌ Rate value is null/undefined for row:', row);
          }
          
          const finalRate = rateValue !== null && rateValue !== undefined ? Number(rateValue) : 0;
          
          return {
            type: normalizedType,
            rate: finalRate,
            description: normalizedType === 'PRO' ? 'Tasa PRO' : 'Tasa Normal',
            updatedAt: row.updated_at || rowAny.updatedAt
          } as InterestRate;
        });
        console.log('✅ Parsed updated rates:', parsed);
        console.log('📈 Setting rates state with:', parsed);
        setRates(parsed);
        setEditValues({
          PRO: parsed.find(r => r.type === 'PRO')?.rate ?? DEFAULT_RATES[0].rate,
          Normal: parsed.find(r => r.type === 'Normal')?.rate ?? DEFAULT_RATES[1].rate
        });
        console.log('✅ Rates state updated');
      } else {
        console.warn('⚠️ No rates data received after update');
      }
      
      setEditingRate(null);
      setSaveMessage({ type: 'success', message: 'Tasa actualizada correctamente' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'No se pudo actualizar la tasa';
      setSaveMessage({ type: 'error', message: errorMessage });
      console.error('Error updating rate:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRateChange = (rateType: 'PRO' | 'Normal', value: string) => {
    const numericValue = parseFloat(value) || 0;
    setEditValues({ ...editValues, [rateType]: numericValue });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="card p-4 sm:p-6">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 sm:h-10 sm:w-10 bg-[#3bbcc8]/10 rounded-lg flex items-center justify-center">
            <Percent className="h-4 w-4 sm:h-5 sm:w-5 text-[#3bbcc8]" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-secondary-900">Tasas de Interés</h2>
            <p className="text-xs sm:text-sm text-secondary-500">Gestiona las tasas de interés para clientes PRO y Normal</p>
          </div>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={`card p-4 ${
          saveMessage.type === 'success' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center space-x-2">
            {saveMessage.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-600" />
            )}
            <span className={`text-sm font-medium ${
              saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {saveMessage.message}
            </span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="card p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#3bbcc8]" />
            <span className="ml-3 text-secondary-600">Cargando tasas de interés...</span>
          </div>
        </div>
      )}

      {/* Interest Rates Cards */}
      {!isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {rates.length === 0 ? (
            <div className="lg:col-span-2 card p-6">
              <div className="text-center py-8">
                <AlertTriangle className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-secondary-900 mb-2">No hay tasas disponibles</h3>
                <p className="text-secondary-600">No se pudieron cargar las tasas de interés.</p>
              </div>
            </div>
          ) : (
            rates.map((rate) => {
              console.log('🎴 Rendering rate card:', rate);
              return (
          <div key={rate.type} className={`card p-6 border-2 ${getRateBgColor(rate.type)}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  rate.type === 'PRO' ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  <TrendingUp className={`h-5 w-5 ${getRateColor(rate.type)}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${getRateColor(rate.type)}`}>
                    Tasa {rate.type}
                  </h3>
                  {rate.updatedAt && (
                    <p className="text-xs text-secondary-500">
                      Actualizó: {new Date(rate.updatedAt).toLocaleDateString('es-MX')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Tasa de Interés Anual
                </label>
                {editingRate === rate.type ? (
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="input-field pr-8"
                        value={editValues[rate.type] || ''}
                        onChange={(e) => handleRateChange(rate.type, e.target.value)}
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400">
                        %
                      </span>
                    </div>
                    <button
                      onClick={() => handleSave(rate.type)}
                      disabled={isSaving}
                      className="btn-primary flex items-center space-x-1 px-3 py-2"
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleCancel(rate.type)}
                      className="btn-secondary px-3 py-2"
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`text-2xl font-bold ${getRateColor(rate.type)}`}>
                        {rate.rate != null && rate.rate !== undefined ? `${Number(rate.rate).toFixed(2)}%` : 'N/A'}
                      </span>
                      <span className="text-sm text-secondary-500">anual</span>
                    </div>
                    <button
                      onClick={() => handleEdit(rate.type, rate.rate)}
                      className="btn-secondary flex items-center space-x-1 px-3 py-2"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>Editar</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-sm text-secondary-600">
                  {rate.description}
                </p>
              </div>

            </div>
          </div>
            );
            })
          )}
        </div>
      )}

      {/* Information Section */}
      <div className="card p-4 sm:p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-800 mb-2">Información Importante</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Las tasas de interés se aplican de forma anual</li>
                <li>• Los cambios en las tasas afectan a nuevos créditos únicamente</li>
                <li>• Los créditos existentes mantienen su tasa original</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasasInteresSection;

